import { useCallback, useEffect, useRef, useState } from 'react';

import { useAutoSave } from '@hooks/useAutoSave';
import type { AutoSaveStatus } from '@hooks/useAutoSave';

export interface UseSessionProviderMappingOptions {
  /** The session being reviewed. Hydration + local state reset when it changes. */
  sessionId: string;
  /**
   * The mapping persisted on the session (`summary.provider_mapping`).
   * `undefined` while the summary is still loading; `{}` once loaded-but-empty.
   */
  persistedMapping: Record<string, string> | undefined;
  /** Persist the whole map to the session. Rejects on failure. */
  persist: (mapping: Record<string, string>) => Promise<unknown>;
  /** Only autosave while the commit flow (where the panel lives) is shown. */
  enabled: boolean;
  /** Debounce before autosaving an edit. */
  debounceMs?: number;
}

export interface UseSessionProviderMapping {
  providerMapping: Record<string, string>;
  /**
   * Clear the local map AND cancel any pending debounced save. For the
   * post-commit reset: must not leave a scheduled save that would write the
   * stale pre-reset map back after the reset.
   */
  resetProviderMapping: () => void;
  /** Operator edited the map: update locally and autosave (debounced). */
  onProviderMappingChange: (mapping: Record<string, string>) => void;
  /** Re-attempt the save of the current map (for the AutosaveStatus retry). */
  retrySave: () => void;
  saveStatus: AutoSaveStatus;
  savedAt: Date | null;
  isDirty: boolean;
}

/**
 * Owns the import provider mapping (source provider key -> platform user id)
 * for a staged session (GDN-2382). The map is persisted on the session, so it
 * survives reloads and is shared with commit + MCP tooling. This hook hydrates
 * the local editing copy from the session summary exactly once per session,
 * then autosaves operator edits back to the session.
 *
 * Hydration is keyed to `sessionId` rather than to the component instance.
 * That was once load-bearing: the review panel outlived a session change,
 * because `dsession` changed in place, so a per-instance guard would have left
 * the new session showing — and autosaving — the previous session's map.
 *
 * Since GDN-3883 the panel's host is keyed by session and does remount, so the
 * only production caller can no longer reach that path. The key stays because
 * it is the honest invariant for a hook that takes a session id, and because a
 * future caller may not be keyed. The remount is also an improvement here:
 * `useAutoSave`'s `flushOnUnmount` now writes a pending edit against the
 * session it belongs to, rather than cancelling it.
 */
export function useSessionProviderMapping({
  sessionId,
  persistedMapping,
  persist,
  enabled,
  debounceMs = 1500,
}: UseSessionProviderMappingOptions): UseSessionProviderMapping {
  const [providerMapping, setProviderMapping] = useState<
    Record<string, string>
  >({});
  const [isDirty, setIsDirty] = useState(false);

  const {
    triggerAutoSave,
    saveImmediately,
    cancelPendingSave,
    status: saveStatus,
    lastSavedAt: savedAt,
  } = useAutoSave<Record<string, string>>({
    onSave: async mapping => {
      await persist(mapping);
    },
    debounceMs,
    enabled,
    flushOnUnmount: true,
  });

  // Reset when the session changes in place (no remount). Drop the old map and
  // any pending save — a save scheduled for the old session would otherwise
  // fire against the new session's record — then allow a fresh hydration.
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedForRef.current === null) return; // never hydrated yet
    if (hydratedForRef.current === sessionId) return; // same session
    hydratedForRef.current = null;
    cancelPendingSave();
    setProviderMapping({});
    setIsDirty(false);
  }, [sessionId, cancelPendingSave]);

  // Hydrate once per session from its persisted map. A refetch hands back a
  // fresh object each time; the guard keeps a later refetch from clobbering the
  // operator's in-progress edits.
  useEffect(() => {
    if (hydratedForRef.current === sessionId) return;
    if (persistedMapping === undefined) return;
    hydratedForRef.current = sessionId;
    if (Object.keys(persistedMapping).length > 0) {
      setProviderMapping(persistedMapping);
    }
  }, [sessionId, persistedMapping]);

  const onProviderMappingChange = useCallback(
    (next: Record<string, string>) => {
      setProviderMapping(next);
      setIsDirty(true);
      triggerAutoSave(next);
    },
    [triggerAutoSave],
  );

  const resetProviderMapping = useCallback(() => {
    cancelPendingSave();
    setProviderMapping({});
    setIsDirty(false);
  }, [cancelPendingSave]);

  const retrySave = useCallback(() => {
    saveImmediately(providerMapping);
  }, [saveImmediately, providerMapping]);

  useEffect(() => {
    if (saveStatus === 'saved') setIsDirty(false);
  }, [saveStatus]);

  return {
    providerMapping,
    resetProviderMapping,
    onProviderMappingChange,
    retrySave,
    saveStatus,
    savedAt,
    isDirty,
  };
}
